import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { parseDateAndTime, dateToLocalDateString, normalizeTimeString } from '@/lib/dateTimeUtils';
import { PLATFORMS } from '@/lib/platformConstants';

interface ParsedPaymentData {
  date: string;
  platform: string;
  total_net_sales: number;
  total_sales_tax: number;
  total_tips: number;
  total_tickets: number;
}

/**
 * Convert Excel time serial number to HH:MM:SS format
 * Excel stores times as decimal fractions of a day
 */
function excelTimeToString(excelTime: any): string | undefined {
  if (!excelTime || excelTime === '') return undefined;
  
  if (typeof excelTime === 'string' && excelTime.includes(':')) {
    return normalizeTimeString(excelTime) ?? excelTime;
  }
  
  // Convert Excel serial number to time
  const timeValue = parseFloat(excelTime);
  if (isNaN(timeValue)) return undefined;
  
  // Excel time is a fraction of a day (0.5 = 12:00:00)
  const totalSeconds = Math.round(timeValue * 24 * 60 * 60);
  const hours = Math.floor(totalSeconds / 3600) % 24;
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

interface DetailedTransactionData {
  platform: string;
  source: string;
  location?: string;
  shop_id?: string;
  order_date?: string;
  order_time?: string;
  sale_date?: string;
  sale_time?: string;
  payout_date?: string;
  payout_time?: string;

  // DoorDash specific
  subtotal_before_discounts?: number;
  tip_amount?: number;
  commission?: number;
  marketing_fees?: number;
  net_payout_amount?: number;
  taxable_subtotal?: number;
  
  // Toast specific
  total_number_of_payments?: number;
  total_payments_amount?: number;
  transaction_fees?: number;
  net_deposit_amount?: number;
  
  // Uber specific
  gross_sales?: number;
  merchant_promotions?: number;
  additional_merchant_promotions?: number;
  uber_promotion_credit?: number;
  net_sale_amount_paid_by_customer?: number;
  uber_commission?: number;
  miscellaneous_deductions?: number;
  total_payout_amount?: number;

  // Shared deduction fields
  price_adjustments?: number;
  promotions_on_delivery?: number;
}

/**
 * Detects platform from header columns by checking ALL required columns from actual files
 * Also accepts optional second row headers for Uber which has descriptions in row 1
 */
export function detectPlatformFromHeaders(headers: any[], headers2?: any[]): 'toast' | 'uber_eats' | 'doordash' | null {
  const headerStr = headers.map(h => String(h || '').toLowerCase().trim()).join('|');
  const headerStr2 = headers2 ? headers2.map(h => String(h || '').toLowerCase().trim()).join('|') : '';
  
  // Toast Payments - Actual headers: Location, Order #, Order Date, Amount, Tip, Total
  const toastRequired = ['location', 'order #', 'order date', 'amount', 'tip', 'total'];
  const hasAllToast = toastRequired.every(col => headerStr.includes(col));
  if (hasAllToast) {
    return 'toast';
  }
  
  // Uber Payments - Row 1 has descriptions, Row 2 has actual headers
  // Row 2 headers: Store Name, Store ID, Order ID, Workflow ID, Dining Mode, Payment Mode, 
  //               Order Channel, Order Status, Order Date, Order Accept Time, etc.
  const uberRequired = ['store name', 'order id', 'order date'];
  const uberRequired2 = ['store name', 'store id', 'order id', 'order status', 'order date'];
  
  // Check row 1 (original format)
  const hasAllUberRow1 = uberRequired.every(col => headerStr.includes(col)) &&
                         (headerStr.includes('sales (excl. tax)') || headerStr.includes('marketplace fee'));
  if (hasAllUberRow1) {
    return 'uber_eats';
  }
  
  // Check row 2 (new format where row 1 has descriptions)
  if (headers2) {
    const hasAllUberRow2 = uberRequired2.every(col => headerStr2.includes(col));
    if (hasAllUberRow2) {
      return 'uber_eats';
    }
  }
  
  // Also check if row 1 contains Uber-specific description text
  if (headerStr.includes('store name as per uber') || headerStr.includes('uber eats manager')) {
    return 'uber_eats';
  }
  
  // DoorDash Payments - Three known CSV formats
  // Old format: has "Subtotal tax passed to merchant"
  const doordashOldRequired = ['business name', 'store name', 'subtotal', 
                              'subtotal tax passed to merchant', 'staff tip', 'commission', 'net total'];
  // XLSX format: has "Timestamp local date" + "Order received local time" as separate columns
  const doordashXlsxRequired = ['timestamp local date', 'order received local time', 'payout date', 
                              'business name', 'subtotal', 'staff tip', 'commission', 'marketing fees', 'net total'];
  // New CSV format (2026+): has "Timestamp local time" (combined), "Tax (subtotal)", "Payout date"
  const doordashCsvRequired = ['business name', 'store name', 'subtotal', 'staff tip', 
                              'commission', 'marketing fees', 'net total', 'payout date'];
  const hasAllDoorDashOld = doordashOldRequired.every(col => headerStr.includes(col));
  const hasAllDoorDashXlsx = doordashXlsxRequired.every(col => headerStr.includes(col));
  const hasAllDoorDashCsv = doordashCsvRequired.every(col => headerStr.includes(col));
  if (hasAllDoorDashOld || hasAllDoorDashXlsx || hasAllDoorDashCsv) {
    return 'doordash';
  }
  
  return null;
}

/**
 * Parse Toast Payments file - Detailed version
 * Uses header-based lookup to handle both formats:
 *   CSV (6 cols):  Location, Order #, Order Date, Amount, Tip, Total
 *   XLSX (12 cols): Location, Order #, Order Date, Amount, Tip, Gratuity, Total, ...
 */
function parseToastPaymentsDetailed(rows: any[]): DetailedTransactionData[] {
  const transactions: DetailedTransactionData[] = [];
  const headerRow = rows[0] || [];
  const headerMap = buildHeaderMap(headerRow);

  const colLocation = findCol(headerMap, 'location') ?? 0;
  const colOrderNum = findCol(headerMap, 'order #', 'order number');
  const colOrderDate = findCol(headerMap, 'order date') ?? 2;
  const colAmount = findCol(headerMap, 'amount') ?? 3;
  const colTip = findCol(headerMap, 'tip') ?? 4;
  const colGratuity = findCol(headerMap, 'gratuity');
  const colTotal = findCol(headerMap, 'total') ?? (colGratuity !== undefined ? colGratuity + 1 : 5);

  const hasGratuity = colGratuity !== undefined;
  console.log(`Toast Payments: header-based lookup. HasGratuity=${hasGratuity}, Amount@${colAmount}, Tip@${colTip}, Gratuity@${colGratuity ?? 'N/A'}, Total@${colTotal}`);

  const toastKeyCounter = new Map<string, number>();
  for (let index = 1; index < rows.length; index++) {
    const row = rows[index];
    if (!row || !row[colLocation] || !row[colOrderDate]) continue;
    if (String(row[colOrderDate]).toLowerCase() === 'order date') continue;

    const location = String(row[colLocation] || '').trim();
    const orderNum = colOrderNum !== undefined ? String(row[colOrderNum] || '').trim() : undefined;
    const orderDateRaw = row[colOrderDate];
    const amount = parseFloat(row[colAmount]) || 0;
    const tip = parseFloat(row[colTip]) || 0;
    const gratuity = hasGratuity ? (parseFloat(row[colGratuity!]) || 0) : 0;
    const total = parseFloat(row[colTotal]) || 0;

    try {
      const parsed = parseDateAndTime(orderDateRaw);
      const parsedOrderDate = parsed.date;
      if (!parsedOrderDate) continue;
      const orderTime = parsed.time ?? undefined;

      const baseId = orderNum || `toast-${index}`;
      const dedupKey = `${parsedOrderDate}|${orderTime ?? ''}|${baseId}`;
      const count = (toastKeyCounter.get(dedupKey) || 0) + 1;
      toastKeyCounter.set(dedupKey, count);
      const shopId = count > 1 ? `${baseId}-${count}` : baseId;

      transactions.push({
        platform: PLATFORMS.TOAST,
        source: 'Manual',
        location,
        shop_id: shopId,
        order_date: parsedOrderDate,
        order_time: orderTime,
        sale_date: parsedOrderDate,
        sale_time: orderTime,
        gross_sales: amount,
        tip_amount: tip + gratuity,
        total_payments_amount: total,
      });
    } catch (error) {
      console.error('Error parsing Toast transaction at row', index, ':', error, row);
    }
  }

  console.log(`Toast Payments: Parsed ${transactions.length} transactions`);
  return transactions;
}

/**
 * Parse Toast Payments file - Summary version (for backward compatibility)
 */
function parseToastPayments(rows: any[]): ParsedPaymentData[] {
  const dailyMap = new Map<string, ParsedPaymentData>();

  rows.forEach(row => {
    // Skip header rows
    if (!row[2] || row[2] === 'Order Date') return;

    const orderDate = row[2]; // Column C (Order Date)
    const amount = parseFloat(row[3]) || 0; // Column D (Amount)
    const tip = parseFloat(row[4]) || 0; // Column E (Tip)

    const parsed = parseDateAndTime(orderDate);
    const date = parsed.date;
    if (!date) return;
    
    // Amount is already the net sales (before tax)
    const netSales = amount;
    const salesTax = 0; // Tax not explicitly provided in Toast file

    if (!dailyMap.has(date)) {
      dailyMap.set(date, {
        date,
        platform: PLATFORMS.TOAST,
        total_net_sales: 0,
        total_sales_tax: 0,
        total_tips: 0,
        total_tickets: 0
      });
    }

    const dayData = dailyMap.get(date)!;
    dayData.total_net_sales += netSales;
    dayData.total_sales_tax += salesTax;
    dayData.total_tips += tip;
    dayData.total_tickets += 1;
  });

  return Array.from(dailyMap.values());
}

/**
 * Parse Uber Payments file - Detailed version
 * Handles two formats:
 * 1. Old format: Headers in row 1, data starts row 2
 * 2. New format: Descriptions in row 1, headers in row 2, data starts row 3
 * 
 * Columns (0-indexed): A (Location), I (Sale Date), J (Sale Time), L (Gross Sales), 
 * T (Merchant Promotions), V (Additional Merchant Promotions), Y (Uber Promotion Credit),
 * Z (Net Sale Amount), AA (Uber Commission), AL (Miscellaneous Deductions), 
 * AO (Total Payout Amount), AP (Payout Date)
 */
function parseUberPaymentsDetailed(rows: any[], salesTaxRate?: number): DetailedTransactionData[] {
  const transactions: DetailedTransactionData[] = [];
  
  // Detect if this is new format (descriptions in row 1, headers in row 2)
  // New format has descriptive text like "Store name as per Uber Eats manager" in row 1
  const row1 = rows[0] || [];
  const row1Str = row1.map((h: any) => String(h || '').toLowerCase()).join('|');
  const isNewFormat = row1Str.includes('uber eats manager') || row1Str.includes('as per uber');
  
  // Determine which row has the actual headers and where data starts
  const headerRowIndex = isNewFormat ? 1 : 0;
  const dataStartIndex = isNewFormat ? 2 : 1;
  const headerRow = rows[headerRowIndex] || [];
  
  console.log(`Uber Payments: New format: ${isNewFormat}, Header row: ${headerRowIndex}, Data starts: ${dataStartIndex}`);
  console.log(`Uber Payments: Headers: ${headerRow.slice(0, 10).join(', ')}`);
  
  const headerMap = buildHeaderMap(headerRow);
  
  const colLocation = findCol(headerMap, 'store name') ?? 0;
  const colOrderId = findCol(headerMap, 'order id');
  const colOrderDate = findCol(headerMap, 'order date') ?? 8;
  const colOrderTime = findCol(headerMap, 'order accept time') ?? 9;
  const colSalesExclTax = findCol(headerMap, 'sales (excl. tax)', 'sales excl. tax', 'total item sales excl tax') ?? 11;
  const colTaxOnSales = findCol(headerMap, 'tax on sales') ?? 12;
  const colPromotionsOnItems = findCol(headerMap, 'offers on items', 'promotions on items') ?? 19;
  const colTaxOnOffers = findCol(headerMap, 'tax on offers on items');
  const colOrderErrorAdj = findCol(headerMap, 'order error adjustments');
  const colDeliveryOffers = findCol(headerMap, 'delivery offer redemptions') ?? 21;
  const colMarketingAdj = findCol(headerMap, 'marketing adjustment') ?? 24;
  const colTotalSalesAfterAdj = findCol(headerMap, 'total sales after adjustments') ?? 25;
  const colMarketplaceFee = findCol(headerMap, 'marketplace fee') ?? 26;
  const colTips = findCol(headerMap, 'tips') ?? 33;
  const colOtherPayments = findCol(headerMap, 'other payments') ?? 37;
  const colTotalPayout = findCol(headerMap, 'total payout') ?? 40;
  const colPayoutDate = findCol(headerMap, 'payout date') ?? 41;

  const uberKeyCounter = new Map<string, number>();
  for (let index = dataStartIndex; index < rows.length; index++) {
    const row = rows[index];
    
    // Skip empty rows
    if (!row || !row[colOrderDate]) continue;
    
    // Skip if this is still a header row
    const orderDateVal = row[colOrderDate];
    if (String(orderDateVal).toLowerCase() === 'order date') continue;

    const location = row[colLocation];
    const orderIdRaw = colOrderId != null ? String(row[colOrderId] || '').trim() : '';
    const orderId = orderIdRaw || undefined;
    const saleDate = orderDateVal;
    const saleTime = excelTimeToString(row[colOrderTime]);
    const grossSales = parseFloat(row[colSalesExclTax]) || 0;
    const offersInclTax = Math.abs(parseFloat(row[colPromotionsOnItems]) || 0);
    const taxOnOffers = colTaxOnOffers != null ? Math.abs(parseFloat(row[colTaxOnOffers]) || 0) : 0;
    let merchantPromotions: number;
    if (taxOnOffers > 0) {
      merchantPromotions = offersInclTax - taxOnOffers;
    } else if (offersInclTax > 0 && salesTaxRate && salesTaxRate > 0) {
      merchantPromotions = offersInclTax / (1 + salesTaxRate);
    } else {
      merchantPromotions = offersInclTax;
    }
    const orderErrorAdj = colOrderErrorAdj != null ? Math.abs(parseFloat(row[colOrderErrorAdj]) || 0) : 0;
    const additionalMerchantPromotions = Math.abs(parseFloat(row[colDeliveryOffers]) || 0);
    const uberPromotionCredit = parseFloat(row[colMarketingAdj]) || 0;
    const netSaleAmount = parseFloat(row[colTotalSalesAfterAdj]) || 0;
    const uberCommission = parseFloat(row[colMarketplaceFee]) || 0;
    const tips = parseFloat(row[colTips]) || 0;
    const miscellaneousDeductions = parseFloat(row[colOtherPayments]) || 0;
    const totalPayoutAmount = parseFloat(row[colTotalPayout]) || 0;
    const payoutDate = row[colPayoutDate];

    // Skip rows with no sales data
    if (grossSales === 0 && totalPayoutAmount === 0) continue;

    try {
      // Parse date (and time if combined): platform may send combined datetime in Order Date column
      const parsedOrderDate = parseDateAndTime(saleDate);
      const parsedSaleDate = parsedOrderDate.date;
      if (!parsedSaleDate) continue;
      const saleTimeFromDate = parsedOrderDate.time ?? undefined;
      const effectiveSaleTime = saleTime ?? saleTimeFromDate;

      let parsedPayoutDate: string | undefined;
      if (payoutDate) {
        const parsed = parseDateAndTime(payoutDate);
        parsedPayoutDate = parsed.date ?? undefined;
      }
      
      const uberBaseId = orderId || `uber-${index}`;
      const uberDedupKey = `${parsedSaleDate}|${effectiveSaleTime ?? ''}|${uberBaseId}`;
      const uberCount = (uberKeyCounter.get(uberDedupKey) || 0) + 1;
      uberKeyCounter.set(uberDedupKey, uberCount);
      const uberShopId = uberCount > 1 ? `${uberBaseId}-${uberCount}` : uberBaseId;

      transactions.push({
        platform: PLATFORMS.UBER,
        source: 'Manual',
        location,
        shop_id: uberShopId,
        order_date: parsedSaleDate,
        order_time: effectiveSaleTime,
        sale_date: parsedSaleDate,
        sale_time: effectiveSaleTime,
        payout_date: parsedPayoutDate,
        gross_sales: grossSales,
        merchant_promotions: merchantPromotions,
        additional_merchant_promotions: additionalMerchantPromotions,
        uber_promotion_credit: uberPromotionCredit,
        price_adjustments: orderErrorAdj,
        net_sale_amount_paid_by_customer: netSaleAmount,
        uber_commission: uberCommission,
        tip_amount: tips,
        miscellaneous_deductions: miscellaneousDeductions,
        total_payout_amount: totalPayoutAmount,
      });
    } catch (error) {
      console.error('Error parsing Uber transaction at row', index, ':', error, row);
    }
  }

  console.log(`Uber Payments: Parsed ${transactions.length} transactions`);
  return transactions;
}

/**
 * Parse Uber Payments file - Summary version (for backward compatibility)
 * Handles both old and new formats (descriptions in row 1 vs headers in row 1)
 */
function parseUberPayments(rows: any[]): ParsedPaymentData[] {
  const dailyMap = new Map<string, ParsedPaymentData>();
  
  // Detect if this is new format (descriptions in row 1, headers in row 2)
  const row1 = rows[0] || [];
  const row1Str = row1.map((h: any) => String(h || '').toLowerCase()).join('|');
  const isNewFormat = row1Str.includes('uber eats manager') || row1Str.includes('as per uber');
  
  const headerRowIndex = isNewFormat ? 1 : 0;
  const dataStartIndex = isNewFormat ? 2 : 1;
  const headerRow = rows[headerRowIndex] || [];
  
  const headerMap = buildHeaderMap(headerRow);
  
  const colOrderDate = findCol(headerMap, 'order date') ?? 8;
  const colSalesExclTax = findCol(headerMap, 'sales (excl. tax)', 'sales excl. tax', 'total item sales excl tax') ?? 11;
  const colTaxOnSales = findCol(headerMap, 'tax on sales') ?? 12;
  const colTips = findCol(headerMap, 'tips') ?? 33;

  for (let i = dataStartIndex; i < rows.length; i++) {
    const row = rows[i];
    
    // Skip empty rows or header rows
    if (!row || !row[colOrderDate]) continue;
    if (String(row[colOrderDate]).toLowerCase() === 'order date') continue;

    const orderDate = row[colOrderDate];
    const salesExclTax = parseFloat(row[colSalesExclTax]) || 0;
    const taxOnSales = parseFloat(row[colTaxOnSales]) || 0;
    const tips = parseFloat(row[colTips]) || 0;

    try {
      const parsed = parseDateAndTime(orderDate);
      const date = parsed.date;
      if (!date) continue;

      if (!dailyMap.has(date)) {
        dailyMap.set(date, {
          date,
          platform: PLATFORMS.UBER,
          total_net_sales: 0,
          total_sales_tax: 0,
          total_tips: 0,
          total_tickets: 0
        });
      }

      const dayData = dailyMap.get(date)!;
      dayData.total_net_sales += salesExclTax;
      dayData.total_sales_tax += taxOnSales;
      dayData.total_tips += tips;
      dayData.total_tickets += 1;
    } catch (error) {
      console.error('Error parsing Uber summary row:', error);
    }
  }

  return Array.from(dailyMap.values());
}

/**
 * Build a header-index map from a row of header values.
 */
export function buildHeaderMap(headerRow: any[]): Record<string, number> {
  const map: Record<string, number> = {};
  headerRow.forEach((h: any, i: number) => {
    const key = String(h || '').toLowerCase().trim();
    if (key) map[key] = i;
  });
  return map;
}

/**
 * Find a column index by trying exact match then prefix match for each candidate name.
 * Handles headers like "Offers on items (incl. tax)" matching a lookup for "offers on items".
 */
export function findCol(headerMap: Record<string, number>, ...candidates: string[]): number | undefined {
  for (const name of candidates) {
    if (headerMap[name] !== undefined) return headerMap[name];
    // Prefix match: "staff tip" matches "staff tip (incl. tax)" etc.
    for (const key of Object.keys(headerMap)) {
      if (key.startsWith(name)) return headerMap[key];
    }
  }
  return undefined;
}

/**
 * Parse DoorDash Payments file - Detailed version
 * Uses header-based column lookup to support all known DoorDash CSV/XLSX formats:
 * - Old XLSX (40+ cols): "Timestamp local date", "Order received local time", "Subtotal tax passed to merchant"
 * - New CSV (29 cols): "Timestamp local time" (combined), "Tax (subtotal)", "Payout date"
 */
function parseDoorDashPaymentsDetailed(rows: any[]): DetailedTransactionData[] {
  const transactions: DetailedTransactionData[] = [];
  const headerRow = rows[0] || [];
  const headerMap = buildHeaderMap(headerRow);

  // Resolve columns with fallbacks across all known format variants
  const colTimestampLocalDate = findCol(headerMap, 'timestamp local date');
  const colTimestampLocalTime = findCol(headerMap, 'timestamp local time');
  const colOrderReceivedTime = findCol(headerMap, 'order received local time');
  const colPayoutTime = findCol(headerMap, 'payout time');
  const colPayoutDate = findCol(headerMap, 'payout date') ?? findCol(headerMap, 'payout date');
  const colBusinessName = findCol(headerMap, 'business name');
  const colStoreName = findCol(headerMap, 'store name');
  const colTransactionType = findCol(headerMap, 'transaction type');
  const colDDOrderId = findCol(headerMap, 'doordash order id');
  const colSubtotal = findCol(headerMap, 'subtotal');
  const colTaxSubtotal = findCol(headerMap, 'tax (subtotal)', 'subtotal tax passed to merchant');
  const colStaffTip = findCol(headerMap, 'staff tip');
  const colCommission = findCol(headerMap, 'commission');
  const colMarketingFees = findCol(headerMap, 'marketing fees');
  const colNetTotal = findCol(headerMap, 'net total');
  const colSubtotalForTax = findCol(headerMap, 'subtotal for tax');
  const colCustomerDiscounts = findCol(headerMap, 'customer discounts', 'customer discounts from marketing');
  const colDDMarketingCredit = findCol(headerMap, 'doordash marketing credit', 'customer discounts from marketing | (funded by doordash)');
  const colErrorCharges = findCol(headerMap, 'error charges');
  const colAdjustments = findCol(headerMap, 'adjustments');

  console.log(`DoorDash Payments: Using header-based columns. Subtotal@${colSubtotal}, Tip@${colStaffTip}, Commission@${colCommission}, Net@${colNetTotal}, CustDisc@${colCustomerDiscounts ?? 'N/A'}, DDCredit@${colDDMarketingCredit ?? 'N/A'}`);

  for (let index = 1; index < rows.length; index++) {
    const row = rows[index];
    if (!row) continue;

    // Note: Error Charge rows are now included -- their deductions are captured in price_adjustments

    // Determine order date: prefer separate "Timestamp local date", fall back to combined "Timestamp local time"
    let orderDateRaw: any;
    let orderTimeRaw: any;

    if (colTimestampLocalDate !== undefined) {
      orderDateRaw = row[colTimestampLocalDate];
      orderTimeRaw = colOrderReceivedTime !== undefined ? row[colOrderReceivedTime] : undefined;
    } else if (colTimestampLocalTime !== undefined) {
      // Combined datetime column (e.g. "2026-01-31 22:13:34.903926")
      orderDateRaw = row[colTimestampLocalTime];
      orderTimeRaw = undefined; // time will be extracted from the combined value
    } else {
      continue;
    }

    if (!orderDateRaw) continue;

    const payoutDateRaw = colPayoutDate !== undefined ? row[colPayoutDate] : undefined;
    const payoutTimeRaw = colPayoutTime !== undefined ? row[colPayoutTime] : undefined;
    const location = colBusinessName !== undefined ? row[colBusinessName] : (colStoreName !== undefined ? row[colStoreName] : undefined);
    const ddOrderIdRaw = colDDOrderId !== undefined ? String(row[colDDOrderId] || '').trim() : undefined;
    const txnType = colTransactionType !== undefined ? String(row[colTransactionType] || '').trim().toLowerCase().replace(/\s+/g, '_') : '';
    const ddOrderId = ddOrderIdRaw
      ? (txnType && txnType !== 'order' ? `${ddOrderIdRaw}-${txnType}` : ddOrderIdRaw)
      : undefined;
    const subtotal = parseFloat(colSubtotal !== undefined ? row[colSubtotal] : 0) || 0;
    const taxSubtotal = parseFloat(colTaxSubtotal !== undefined ? row[colTaxSubtotal] : 0) || 0;
    const tip = parseFloat(colStaffTip !== undefined ? row[colStaffTip] : 0) || 0;
    const commission = parseFloat(colCommission !== undefined ? row[colCommission] : 0) || 0;
    const marketingFees = parseFloat(colMarketingFees !== undefined ? row[colMarketingFees] : 0) || 0;
    const netTotal = parseFloat(colNetTotal !== undefined ? row[colNetTotal] : 0) || 0;
    const taxableSubtotal = colSubtotalForTax !== undefined ? (parseFloat(row[colSubtotalForTax]) || 0) : taxSubtotal;

    let orderTime: string | undefined = undefined;
    if (orderTimeRaw) {
      orderTime = excelTimeToString(orderTimeRaw);
      if (!orderTime) {
        const normalized = normalizeTimeString(orderTimeRaw);
        if (normalized) orderTime = normalized;
      }
    }

    let payoutTime: string | undefined = undefined;
    if (payoutTimeRaw) {
      payoutTime = excelTimeToString(payoutTimeRaw);
      if (!payoutTime && typeof payoutTimeRaw === 'string' && payoutTimeRaw.includes(':')) {
        const timeParts = payoutTimeRaw.split(' ');
        payoutTime = (timeParts.length > 1 ? timeParts[1] : payoutTimeRaw).split('.')[0];
      }
    }

    try {
      const parsedOrder = parseDateAndTime(orderDateRaw);
      const parsedOrderDate = parsedOrder.date;
      if (!parsedOrderDate) continue;
      const orderTimeFromDate = parsedOrder.time;
      const effectiveOrderTime = orderTime ?? orderTimeFromDate ?? undefined;

      let parsedPayoutDate: string | undefined = undefined;
      if (payoutDateRaw && payoutDateRaw !== 'NULL' && String(payoutDateRaw).trim() !== '') {
        const parsedPayout = parseDateAndTime(payoutDateRaw);
        parsedPayoutDate = parsedPayout.date ?? undefined;
      }

      const customerDiscounts = colCustomerDiscounts !== undefined
        ? Math.abs(parseFloat(row[colCustomerDiscounts]) || 0) : 0;
      const ddMarketingCredit = colDDMarketingCredit !== undefined
        ? Math.abs(parseFloat(row[colDDMarketingCredit]) || 0) : 0;
      const errorCharges = colErrorCharges !== undefined
        ? Math.abs(parseFloat(row[colErrorCharges]) || 0) : 0;
      const adjustments = colAdjustments !== undefined
        ? Math.abs(parseFloat(row[colAdjustments]) || 0) : 0;

      transactions.push({
        platform: PLATFORMS.DOORDASH,
        source: 'Manual',
        location,
        shop_id: ddOrderId || `dd-${index}`,
        order_date: parsedOrderDate,
        order_time: effectiveOrderTime,
        payout_date: parsedPayoutDate,
        payout_time: payoutTime,
        subtotal_before_discounts: subtotal,
        gross_sales: subtotal,
        tip_amount: tip,
        commission: Math.abs(commission),
        marketing_fees: Math.abs(marketingFees),
        merchant_promotions: customerDiscounts,
        promotions_on_delivery: ddMarketingCredit,
        price_adjustments: errorCharges + adjustments,
        net_payout_amount: netTotal,
        taxable_subtotal: taxableSubtotal,
      });
    } catch (error) {
      console.error('Error parsing DoorDash transaction at row', index, ':', error, row);
    }
  }

  console.log(`DoorDash Payments: Parsed ${transactions.length} transactions`);
  return transactions;
}

/**
 * Parse DoorDash Payments file - Summary version (for backward compatibility)
 * Uses header-based lookup to support all known DoorDash formats.
 */
function parseDoorDashPayments(rows: any[]): ParsedPaymentData[] {
  const dailyMap = new Map<string, ParsedPaymentData>();
  const headerRow = rows[0] || [];
  const headerMap = buildHeaderMap(headerRow);

  const colDate = findCol(headerMap, 'timestamp local date', 'timestamp local time');
  const colSubtotal = findCol(headerMap, 'subtotal');
  const colTax = findCol(headerMap, 'tax (subtotal)', 'subtotal tax passed to merchant');
  const colTip = findCol(headerMap, 'staff tip');

  if (colDate === undefined || colSubtotal === undefined) return [];

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[colDate]) continue;

    const localDateRaw = row[colDate];
    const subtotal = parseFloat(row[colSubtotal]) || 0;
    const subtotalTax = colTax !== undefined ? (parseFloat(row[colTax]) || 0) : 0;
    const staffTip = colTip !== undefined ? (parseFloat(row[colTip]) || 0) : 0;

    try {
      const parsed = parseDateAndTime(localDateRaw);
      const date = parsed.date;
      if (!date) continue;

      if (!dailyMap.has(date)) {
        dailyMap.set(date, {
          date,
          platform: PLATFORMS.DOORDASH,
          total_net_sales: 0,
          total_sales_tax: 0,
          total_tips: 0,
          total_tickets: 0
        });
      }

      const dayData = dailyMap.get(date)!;
      dayData.total_net_sales += subtotal;
      dayData.total_sales_tax += subtotalTax;
      dayData.total_tips += staffTip;
      dayData.total_tickets += 1;
    } catch (error) {
      console.error('Error parsing DoorDash summary row:', error);
    }
  }

  return Array.from(dailyMap.values());
}

/**
 * Main processor for payments files - Returns detailed transactions
 */
export async function processPaymentsFileDetailed(file: File, salesTaxRate?: number): Promise<DetailedTransactionData[]> {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  let rows: any[] = [];

  if (fileExtension === 'csv') {
    const text = await file.text();
    const result = Papa.parse(text, { header: false });
    rows = result.data as any[];
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer);
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
  } else {
    throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  }

  // Detect platform from headers (check both row 1 and row 2 for Uber)
  const headers2 = rows.length > 1 ? rows[1] : undefined;
  const platform = detectPlatformFromHeaders(rows[0] || [], headers2);
  
  if (!platform) {
    // Provide helpful error message with detected headers
    const detectedHeaders = (rows[0] || []).slice(0, 8).map((h: any) => String(h || '').substring(0, 30)).join(', ');
    throw new Error(
      `Could not detect platform from file headers. Expected Toast, Uber Eats, or DoorDash payment file. ` +
      `Found headers: ${detectedHeaders}...`
    );
  }

  console.log(`Payments processor detected platform: ${platform}`);

  switch (platform) {
    case 'toast':
      return parseToastPaymentsDetailed(rows);
    case 'uber_eats':
      return parseUberPaymentsDetailed(rows, salesTaxRate);
    case 'doordash':
      return parseDoorDashPaymentsDetailed(rows);
    default:
      throw new Error('Unknown platform');
  }
}

/**
 * Main processor for payments files - Returns summary data (for backward compatibility)
 */
export async function processPaymentsFile(file: File): Promise<ParsedPaymentData[]> {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  let rows: any[] = [];

  if (fileExtension === 'csv') {
    const text = await file.text();
    const result = Papa.parse(text, { header: false });
    rows = result.data as any[];
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer);
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
  } else {
    throw new Error('Unsupported file format. Please upload CSV or Excel files.');
  }

  // Detect platform from headers (check both row 1 and row 2 for Uber)
  const headers2 = rows.length > 1 ? rows[1] : undefined;
  const platform = detectPlatformFromHeaders(rows[0] || [], headers2);
  
  if (!platform) {
    const detectedHeaders = (rows[0] || []).slice(0, 8).map((h: any) => String(h || '').substring(0, 30)).join(', ');
    throw new Error(
      `Could not detect platform from file headers. Expected Toast, Uber Eats, or DoorDash payment file. ` +
      `Found headers: ${detectedHeaders}...`
    );
  }

  console.log(`Payments processor (summary) detected platform: ${platform}`);

  switch (platform) {
    case 'toast':
      return parseToastPayments(rows);
    case 'uber_eats':
      return parseUberPayments(rows);
    case 'doordash':
      return parseDoorDashPayments(rows);
    default:
      throw new Error('Unknown platform');
  }
}
